import { useMemo } from 'react';
import {
    QueryConstraint,
    limit, orderBy, query, where,
} from 'firebase/firestore';
import useCustomers from 'data/firestore/agent/customer/useCollection';
import useSearchCustomers from 'data/hooks/useSearchCustomers';
import { CHAT_PAGE_SIZE } from '../../../../../constants';

const useCustomersWithMessages = ({
    agentId,
    searchQuery = '',
    pageSize = CHAT_PAGE_SIZE,
    activePage = 1,
    showUnreadOnly = false,
}: {
    agentId: string;
    searchQuery?: string;
    showUnreadOnly?: boolean;
    activePage?: number;
    pageSize?: number;
}) => {
    const { data: customers, isLoading: isLoadingCustomers } = useCustomers(
        agentId,
        {
            listQueryFn: (q) => {
                const modifiers: QueryConstraint[] = [orderBy('lastMessage.createdAt', 'desc')];

                if (showUnreadOnly) {
                    modifiers.splice(0, 0, where('hasNewMessage', '==', true));
                }

                return query(
                    q,
                    ...modifiers,
                    limit(pageSize),
                );
            },
            disabled: !agentId || Boolean(searchQuery),
            track: [agentId, showUnreadOnly, pageSize],
        },
    );

    const { data: searchCustomersData, isLoading: isLoadingSearchCustomers } = useSearchCustomers({
        agentId,
        searchQuery,
        pageSize,
        page: activePage,
        sortBy: 'lastMessage.createdAt',
        sortOrder: 'desc',
        hasNewMessage: showUnreadOnly || undefined,
        queryOptions: {
            enabled: !!searchQuery,
        },
    });

    const dataToshow = searchQuery ? searchCustomersData?.hits : customers;
    const isLoading = isLoadingCustomers || isLoadingSearchCustomers;

    return useMemo(
        () => ({
            isLoading,
            customers: dataToshow,
        }),
        [dataToshow, isLoading],
    );
};

export default useCustomersWithMessages;
